class ArticleRelated extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.images = [];
    this.defaultId = "";
    this.onItemEnter = this.onItemEnter.bind(this);
    this.onItemFocus = this.onItemFocus.bind(this);
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll("[data-related-item]"));
    this.images = Array.from(this.querySelectorAll("[data-related-image]"));
    if (!this.items.length || !this.images.length) return;

    const firstImage = this.images.find(
      (image) => image.dataset.relatedId
    );
    this.defaultId = firstImage ? firstImage.dataset.relatedId : "";
    if (this.defaultId) {
      this.setActive(this.defaultId);
    }

    this.items.forEach((item) => {
      item.addEventListener("mouseenter", this.onItemEnter);
      item.addEventListener("focusin", this.onItemFocus);
    });
  }

  disconnectedCallback() {
    this.items.forEach((item) => {
      item.removeEventListener("mouseenter", this.onItemEnter);
      item.removeEventListener("focusin", this.onItemFocus);
    });
  }

  onItemEnter(event) {
    const item = event.currentTarget;
    this.activateItem(item);
  }

  onItemFocus(event) {
    const item = event.currentTarget;
    this.activateItem(item);
  }

  activateItem(item) {
    const id = item?.dataset?.relatedId || "";
    if (!id) return;
    const hasImage = this.images.some(
      (image) => image.dataset.relatedId === id
    );
    if (!hasImage) return;
    this.setActive(id);
  }

  setActive(id) {
    this.images.forEach((image) => {
      const isActive = image.dataset.relatedId === id;
      image.classList.toggle("opacity-70", isActive);
      image.classList.toggle("opacity-0", !isActive);
      image.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    this.items.forEach((item) => {
      const isActive = item.dataset.relatedId === id;
      item.dataset.active = isActive ? "true" : "false";
    });
  }
}

if (!customElements.get("article-related")) {
  customElements.define("article-related", ArticleRelated);
}

if (!customElements.get("article-comments")) {
  class ArticleComments extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.textarea = null;
      this.cancelButton = null;
      this.commentsList = null;
      this.loadMoreButton = null;
      this.loadMoreWrap = null;
      this.onExpand = this.onExpand.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onLoadMore = this.onLoadMore.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector("[data-comment-form]");
      this.textarea = this.querySelector("[data-comment-input]");
      this.cancelButton = this.querySelector("[data-comment-cancel]");
      this.commentsList = this.querySelector("[data-comments-list]");
      this.loadMoreButton = this.querySelector("[data-comments-load-more]");
      this.loadMoreWrap = this.querySelector("[data-comments-load-more-wrap]");
      this.successMessage = this.querySelector(".article-comments__success");
      if (!this.form || !this.textarea) return;

      this.textarea.addEventListener("focus", this.onExpand);
      if (this.cancelButton) {
        this.cancelButton.addEventListener("click", this.onCancel);
      }
      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener("click", this.onLoadMore);
      }
      if (this.successMessage) {
        window.setTimeout(() => {
          this.successMessage.classList.add("hidden");
        }, 5000);
      }
    }

    disconnectedCallback() {
      if (this.textarea) {
        this.textarea.removeEventListener("focus", this.onExpand);
      }
      if (this.cancelButton) {
        this.cancelButton.removeEventListener("click", this.onCancel);
      }
      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener("click", this.onLoadMore);
      }
    }

    onExpand() {
      if (!this.form) return;
      this.form.classList.add("is-expanded");
    }

    onCancel(event) {
      event.preventDefault();
      if (!this.form) return;
      this.form.classList.remove("is-expanded");
      if (this.textarea) {
        this.textarea.blur();
      }
    }

    async onLoadMore(event) {
      event.preventDefault();
      if (!this.loadMoreButton || !this.commentsList) return;
      const nextUrl = this.loadMoreButton.getAttribute("href");
      if (!nextUrl) return;

      this.loadMoreButton.setAttribute("aria-busy", "true");
      this.loadMoreButton.classList.add("opacity-50", "pointer-events-none");

      try {
        if (!window.webvista?.fetchHtml) throw new Error("fetchHtml unavailable");
        const doc = await window.webvista.fetchHtml(nextUrl);
        const nextList = doc.querySelector("[data-comments-list]");
        if (nextList) {
          Array.from(nextList.children).forEach((child) => {
            this.commentsList.appendChild(child);
          });
        }
        const nextLoadMore = doc.querySelector("[data-comments-load-more]");
        if (nextLoadMore && nextLoadMore.getAttribute("href")) {
          this.loadMoreButton.setAttribute(
            "href",
            nextLoadMore.getAttribute("href")
          );
          this.loadMoreButton.classList.remove(
            "opacity-50",
            "pointer-events-none"
          );
          this.loadMoreButton.removeAttribute("aria-busy");
        } else if (this.loadMoreWrap) {
          this.loadMoreWrap.remove();
        }
      } catch (error) {
        console.warn("Failed to load more comments", error);
        this.loadMoreButton.classList.remove(
          "opacity-50",
          "pointer-events-none"
        );
        this.loadMoreButton.removeAttribute("aria-busy");
      }
    }
  }

  customElements.define("article-comments", ArticleComments);
}
